import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { dashboardService, getErrorMessage } from '../services/api';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  Users, 
  Building2, 
  Plug, 
  Clock, 
  Zap, 
  TrendingUp,
  AlertCircle,
  RefreshCw,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Battery,
  BatteryCharging,
  Calendar,
  Loader2,
  Wifi,
  WifiOff,
  Wrench,
  CheckCircle2,
  ClipboardList,
  Newspaper,
  PenSquare
} from 'lucide-react';

// Skeleton Loader Component
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 rounded ${className}`} />
);

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendValue, color, delay = 0 }) => {
  const colorClasses = {
    blue: { icon: 'text-blue-500', bg: 'bg-blue-500/10 dark:bg-blue-500/20', glow: 'shadow-blue-500/20' },
    emerald: { icon: 'text-emerald-500', bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', glow: 'shadow-emerald-500/20' },
    violet: { icon: 'text-violet-500', bg: 'bg-violet-500/10 dark:bg-violet-500/20', glow: 'shadow-violet-500/20' },
    amber: { icon: 'text-amber-500', bg: 'bg-amber-500/10 dark:bg-amber-500/20', glow: 'shadow-amber-500/20' },
    rose: { icon: 'text-rose-500', bg: 'bg-rose-500/10 dark:bg-rose-500/20', glow: 'shadow-rose-500/20' }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div 
      className="group relative p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-300 hover:shadow-lg animate-fade-in"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className={`absolute inset-0 rounded-2xl ${colors.glow} opacity-0 group-hover:opacity-100 transition-opacity blur-xl`} />
      
      <div className="relative flex items-start justify-between">
        <div className="space-y-3">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{title}</p>
          <p className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
              {trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.icon}`} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};

// Station Status Badge
const StatusBadge = ({ status, count }) => {
  const statusConfig = {
    available: { color: 'bg-emerald-500', label: 'Müsait', icon: Battery },
    charging: { color: 'bg-violet-500 animate-pulse', label: 'Şarj Ediyor', icon: BatteryCharging },
    offline: { color: 'bg-slate-400', label: 'Çevrimdışı', icon: Plug },
    faulted: { color: 'bg-rose-500', label: 'Arızalı', icon: AlertCircle },
    error: { color: 'bg-rose-500', label: 'Hata', icon: AlertCircle },
    maintenance: { color: 'bg-amber-500', label: 'Bakımda', icon: Activity }
  };

  const config = statusConfig[status] || statusConfig.offline;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50">
      <div className={`w-3 h-3 rounded-full ${config.color}`} />
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-900 dark:text-white">{config.label}</p>
      </div>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-400" />
        <span className="text-2xl font-bold text-slate-900 dark:text-white">{count}</span>
      </div>
    </div>
  );
};

// Chart Card Component
const ChartCard = ({ title, subtitle, icon: Icon, children, loading }) => (
  <div className="p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50">
    <div className="flex items-center justify-between mb-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-slate-500" />}
          {title}
        </h3>
        {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>}
      </div>
    </div>
    {loading ? (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    ) : (
      children
    )}
  </div>
);

// Period Filter Component
const PeriodFilter = ({ value, onChange }) => (
  <div className="flex items-center gap-2 p-1 rounded-xl bg-slate-100 dark:bg-slate-800">
    {[
      { value: 'today', label: 'Bugun' },
      { value: 'week', label: 'Bu Hafta' },
      { value: 'month', label: 'Bu Ay' }
    ].map((option) => (
      <button
        key={option.value}
        onClick={() => onChange(option.value)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          value === option.value
            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
        <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Pie Chart Colors
const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function DashboardPage() {
  const { user, isAdmin, isSimulationActive, simulatedRole, getEffectiveRole, getEffectiveSiteId, isCalisan, isHesapYoneticisi } = useAuth();
  const [stats, setStats] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expiringContracts, setExpiringContracts] = useState(null);
  const [workOrderStats, setWorkOrderStats] = useState({ pending: 0, completed: 0, active: null });
  
  // Simülasyon için rol kontrolü
  const effectiveRole = getEffectiveRole();
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveCalisan = effectiveRole === ROLES.CALISAN || effectiveRole === 'calisan' || effectiveRole === 'technician';
  const isEffectiveTechnician = effectiveRole === 'technician';
  const isEffectiveHesapYoneticisi = effectiveRole === ROLES.HESAP_YONETICISI || effectiveRole === 'hesap_yoneticisi';
  const isEffectiveIcerikYoneticisi = effectiveRole === ROLES.ICERIK_YONETICISI || effectiveRole === 'icerik_yoneticisi';
  
  // Simülasyonda görüntülenecek isim
  const getDisplayName = () => {
    if (isSimulationActive()) {
      const roleNames = {
        [ROLES.ADMIN]: 'Admin',
        [ROLES.HESAP_YONETICISI]: 'Hesap Yöneticisi',
        [ROLES.YARDIMCI]: 'Yardımcı',
        [ROLES.SITE_SAKINI]: 'Site Sakini',
        [ROLES.CALISAN]: 'Çalışan/Şoför',
        [ROLES.ICERIK_YONETICISI]: 'İçerik Yöneticisi',
        'technician': 'Usta/Teknisyen',
        'calisan': 'Çalışan/Şoför',
        'icerik_yoneticisi': 'İçerik Yöneticisi'
      };
      return roleNames[simulatedRole] || simulatedRole;
    }
    return user?.full_name || user?.email;
  };
  
  // Chart data states
  const [period, setPeriod] = useState('today');
  const [revenueData, setRevenueData] = useState([]);
  const [energyByStationData, setEnergyByStationData] = useState([]);
  const [sessionsBySiteData, setSessionsBySiteData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  const fetchStats = useCallback(async (showRefreshAnim = false) => {
    if (showRefreshAnim) setRefreshing(true);
    setLoading(true);
    setError(null);
    try {
      // Hesap Yöneticisi simülasyonunda site_id parametresi ekle
      let statsPromise;
      let livePromise;
      
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        statsPromise = dashboardService.stats(period, effectiveSiteId);
        livePromise = dashboardService.live(effectiveSiteId).catch(() => null);
      } else {
        statsPromise = dashboardService.stats(period);
        livePromise = dashboardService.live().catch(() => null);
      }
      
      const [statsData, live] = await Promise.all([
        statsPromise,
        livePromise
      ]);
      setStats(statsData);
      
      // Live data'da da filtreleme yap (ekstra güvenlik)
      if (live && isEffectiveHesapYoneticisi && effectiveSiteId) {
        if (live.live_stations) {
          live.live_stations = live.live_stations.filter(s => s.site_id === effectiveSiteId);
        }
        // İstatistikleri güncelle
        if (live.live_stations) {
          live.total_stations = live.live_stations.length;
          live.online_stations = live.live_stations.filter(s => s.is_online).length;
        }
      }
      
      setLiveData(live);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
      if (showRefreshAnim) {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  }, [period, isEffectiveHesapYoneticisi, effectiveSiteId]);

  const fetchChartData = useCallback(async () => {
    setChartsLoading(true);
    try {
      const [revenue, energyByStation, sessionsBySite] = await Promise.all([
        dashboardService.revenueChart(period),
        dashboardService.energyByStationChart(period),
        dashboardService.sessionsBySiteChart(period)
      ]);
      setRevenueData(revenue || []);
      setEnergyByStationData(energyByStation || []);
      setSessionsBySiteData(sessionsBySite || []);
    } catch (err) {
      console.error('Chart data fetch error:', err);
    } finally {
      setChartsLoading(false);
    }
  }, [period]);

  // Sözleşme bitiş tarihi uyarılarını getir (sadece admin için)
  const fetchExpiringContracts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contracts/expiring?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExpiringContracts(data);
      }
    } catch (err) {
      console.error('Contract expiring fetch error:', err);
    }
  }, [isAdmin]);

  // Teknisyen için iş emri istatistiklerini getir
  const fetchWorkOrderStats = useCallback(async () => {
    if (!isEffectiveTechnician) return;
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/work-orders/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // API doğrudan liste döndürüyor
        const orders = Array.isArray(data) ? data : (data.work_orders || []);
        const pending = orders.filter(o => ['assigned', 'pending', 'on_the_way'].includes(o.status)).length;
        const completed = orders.filter(o => ['completed', 'closed', 'paid'].includes(o.status)).length;
        const active = orders.find(o => ['in_progress', 'investigating'].includes(o.status));
        setWorkOrderStats({ pending, completed, active });
      }
    } catch (err) {
      console.error('Work order stats fetch error:', err);
    }
  }, [isEffectiveTechnician]);

  useEffect(() => {
    fetchStats();
    fetchExpiringContracts();
    fetchWorkOrderStats();
    // Auto-refresh every 30 seconds for live data
    const interval = setInterval(() => fetchStats(false), 30000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchExpiringContracts, fetchWorkOrderStats]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // Loading State
  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-24" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-20" />
                </div>
                <Skeleton className="h-12 w-12 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="p-6 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-rose-100 dark:bg-rose-500/20">
              <AlertCircle className="h-5 w-5 text-rose-500" />
            </div>
            <div>
              <p className="font-medium text-rose-700 dark:text-rose-400">Veri yüklenemedi</p>
              <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchStats}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-500/30 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Tekrar Dene</span>
          </button>
        </div>
      </div>
    );
  }

  // Stat Cards Configuration
  const adminStatCards = [
    { title: 'Toplam Kullanıcı', value: stats?.total_users || 0, icon: Users, color: 'blue' },
    { title: 'Toplam Site', value: stats?.total_sites || 0, icon: Building2, color: 'emerald' },
    { title: 'Toplam İstasyon', value: stats?.total_stations || 0, icon: Plug, color: 'violet' },
    { title: 'Aktif Oturum', value: stats?.active_sessions || 0, icon: Clock, color: 'amber' },
    { title: 'Bugünkü Enerji', value: `${(stats?.today_energy || 0).toFixed(1)} kWh`, icon: Zap, color: 'blue', trend: 'up', trendValue: '%12 dün' },
    { title: 'Bugünkü Gelir', value: `${(stats?.today_revenue || 0).toFixed(2)} TL`, icon: TrendingUp, color: 'emerald', trend: 'up', trendValue: '%8 dün' }
  ];

  const userStatCards = [
    { title: 'Aktif Oturum', value: stats?.active_sessions || 0, icon: Clock, color: 'amber' },
    { title: 'Bugünkü Enerji', value: `${(stats?.today_energy || 0).toFixed(1)} kWh`, icon: Zap, color: 'blue' },
    { title: 'Toplam Şarj', value: stats?.user_total_sessions || 0, icon: BarChart3, color: 'violet' },
    { title: 'Toplam Enerji', value: `${(stats?.user_total_energy || 0).toFixed(1)} kWh`, icon: Zap, color: 'emerald' }
  ];

  // Çalışan/Şoför için kartlar - Gelir istatistikleri gizli
  const calisanStatCards = [
    { title: 'Aktif Oturum', value: stats?.active_sessions || 0, icon: Clock, color: 'amber' },
    { title: 'Bugünkü Enerji', value: `${(stats?.today_energy || 0).toFixed(1)} kWh`, icon: Zap, color: 'blue' },
    { title: 'Toplam Şarj', value: stats?.user_total_sessions || 0, icon: BarChart3, color: 'violet' },
    { title: 'Toplam Enerji', value: `${(stats?.user_total_energy || 0).toFixed(1)} kWh`, icon: Zap, color: 'emerald' }
  ];

  // Teknisyen için özel kartlar - şarj istatistikleri yerine iş emirleri
  const technicianStatCards = [
    { title: 'Bekleyen İş Emirleri', value: workOrderStats.pending, icon: ClipboardList, color: 'amber' },
    { title: 'Tamamlanan İşler', value: workOrderStats.completed, icon: CheckCircle2, color: 'emerald' },
    { title: 'Aktif İş Emri', value: workOrderStats.active ? 1 : 0, icon: Wrench, color: 'blue' }
  ];

  // Hesap Yöneticisi için sadece kendi sitesinin verileri gösterilir (backend'den filtreleniyor)
  const hesapYoneticisiStatCards = [
    { title: 'İstasyon Sayısı', value: stats?.total_stations || 0, icon: Plug, color: 'violet' },
    { title: 'Aktif Oturum', value: stats?.active_sessions || 0, icon: Clock, color: 'amber' },
    { title: 'Bugünkü Enerji', value: `${(stats?.today_energy || 0).toFixed(1)} kWh`, icon: Zap, color: 'blue' },
    { title: 'Bugünkü Gelir', value: `${(stats?.today_revenue || 0).toFixed(2)} TL`, icon: TrendingUp, color: 'emerald' }
  ];

  // Görüntülenecek kartları belirle
  const getVisibleCards = () => {
    // İçerik Yöneticisi için istatistik yerine kısayol göster
    if (isEffectiveIcerikYoneticisi) return [];
    if (isEffectiveTechnician) return technicianStatCards;
    // Çalışan/Şoför için gelir istatistiği olmayan kartlar
    if (isEffectiveCalisan || isCalisan()) return calisanStatCards;
    if (isEffectiveHesapYoneticisi || isHesapYoneticisi()) return hesapYoneticisiStatCards;
    if (isAdmin()) return adminStatCards;
    return userStatCards;
  };

  const visibleCards = getVisibleCards();

  // İçerik Yöneticisi için kısayollar
  const contentManagerShortcuts = [
    { name: 'Haber Merkezi', path: '/news', icon: Newspaper, color: 'bg-blue-500', description: 'Haberleri görüntüle ve oku' },
    { name: 'İçerik Yönetimi', path: '/content-management', icon: PenSquare, color: 'bg-emerald-500', description: 'Yeni haber ekle, düzenle' }
  ];

  // İçerik Yöneticisi için özel dashboard
  if (isEffectiveIcerikYoneticisi) {
    return (
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Hoş geldiniz, <span className="text-slate-700 dark:text-slate-300 font-medium">{getDisplayName()}</span>
            {isSimulationActive() && <span className="ml-2 text-red-500 text-sm">(Simülasyon)</span>}
          </p>
        </div>

        {/* Kısayollar */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Hızlı Erişim</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contentManagerShortcuts.map((shortcut, index) => {
              const Icon = shortcut.icon;
              return (
                <a
                  key={index}
                  href={shortcut.path}
                  className="group p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 hover:border-primary/50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-4 rounded-xl ${shortcut.color}/10`}>
                      <Icon className={`h-8 w-8 ${shortcut.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                        {shortcut.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{shortcut.description}</p>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {/* Bilgi Kutusu */}
        <div className="p-6 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <AlertCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-1">
                İçerik Yöneticisi Paneli
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Haber Merkezi'nde yayınlanan içerikleri yönetebilir, yeni haberler ekleyebilir ve mevcut haberleri düzenleyebilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Hoş geldiniz, <span className="text-slate-700 dark:text-slate-300 font-medium">{getDisplayName()}</span>
            {isSimulationActive() && <span className="ml-2 text-red-500 text-sm">(Simülasyon)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodFilter value={period} onChange={setPeriod} />
          <button
            onClick={() => { fetchStats(true); fetchChartData(); }}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Yenileniyor...' : 'Yenile'}</span>
          </button>
        </div>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleCards.map((stat, index) => (
          <StatCard key={index} {...stat} delay={index * 0.05} />
        ))}
      </div>

      {/* Contract Expiration Warnings - Admin only */}
      {isAdmin() && expiringContracts && (expiringContracts.total_expired > 0 || expiringContracts.total_expiring > 0) && (
        <div className="p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700/50">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-amber-500/20">
              <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
                Sözleşme Bitiş Tarihi Uyarıları
              </h3>
              
              {/* Süresi Dolmuş Sözleşmeler */}
              {expiringContracts.expired?.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">
                    Süresi Dolmuş ({expiringContracts.total_expired} site)
                  </p>
                  <div className="space-y-2">
                    {expiringContracts.expired.slice(0, 3).map((contract) => (
                      <div key={contract.site_id} className="flex items-center justify-between p-2 rounded-lg bg-red-100/50 dark:bg-red-900/30">
                        <div>
                          <p className="font-medium text-red-800 dark:text-red-200">{contract.site_name}</p>
                          <p className="text-xs text-red-600 dark:text-red-400">
                            {contract.contract_company} - {Math.abs(contract.days_remaining)} gün önce doldu
                          </p>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500 text-white">
                          Süresi Doldu
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Süresi Yaklaşan Sözleşmeler */}
              {expiringContracts.expiring?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
                    Süresi Yaklaşıyor ({expiringContracts.total_expiring} site, 30 gün içinde)
                  </p>
                  <div className="space-y-2">
                    {expiringContracts.expiring.slice(0, 5).map((contract) => (
                      <div key={contract.site_id} className="flex items-center justify-between p-2 rounded-lg bg-amber-100/50 dark:bg-amber-900/30">
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">{contract.site_name}</p>
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            {contract.contract_company} - Bitiş: {new Date(contract.end_date).toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          contract.days_remaining <= 7 
                            ? 'bg-red-500 text-white' 
                            : contract.days_remaining <= 14 
                              ? 'bg-orange-500 text-white'
                              : 'bg-amber-500 text-white'
                        }`}>
                          {contract.days_remaining} gün kaldı
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <a 
                href="/sites" 
                className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200"
              >
                Tüm Hesapları Görüntüle
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Station Status Overview - Admin only, hide for technician */}
      {isAdmin() && !isEffectiveTechnician && stats?.station_status && Object.keys(stats.station_status).length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">İstasyon Durumu</h2>
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Activity className="h-4 w-4" />
              <span>Anlik durum</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {Object.entries(stats.station_status).map(([status, count]) => (
              <StatusBadge key={status} status={status} count={count} />
            ))}
          </div>
        </div>
      )}

      {/* Charts Section - Hide for technician and calisan/sofor */}
      {!isEffectiveTechnician && !isEffectiveCalisan && !isCalisan() && (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-slate-500" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            {period === 'today' ? 'Bugünkü' : period === 'week' ? 'Haftalık' : 'Aylık'} İstatistikler
          </h2>
        </div>

        {/* Revenue Line Chart */}
        <ChartCard 
          title="Gelir ve Enerji Trendi" 
          subtitle="Zaman icinde gelir ve enerji tuketimi"
          icon={TrendingUp}
          loading={chartsLoading}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="date" 
                stroke="#94a3b8"
                tick={{ fill: '#94a3b8', fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left"
                stroke="#3b82f6"
                tick={{ fill: '#94a3b8', fontSize: 12 }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                stroke="#10b981"
                tick={{ fill: '#94a3b8', fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="revenue" 
                name="Gelir (TL)"
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#3b82f6' }}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="energy" 
                name="Enerji (kWh)"
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#10b981' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Energy by Station Bar Chart */}
          <ChartCard 
            title="İstasyon Bazlı Enerji" 
            subtitle="En çok kullanılan istasyonlar"
            icon={Zap}
            loading={chartsLoading}
          >
            {energyByStationData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-slate-500">
                <p>Bu dönem için veri bulunamadı</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={energyByStationData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis 
                    dataKey="station" 
                    type="category" 
                    stroke="#94a3b8"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    width={80}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="energy" 
                    name="Enerji (kWh)"
                    fill="#8b5cf6" 
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Sessions by Site Pie Chart */}
          <ChartCard 
            title="Site Bazli Oturumlar" 
            subtitle="Sitelere gore oturum dagilimi"
            icon={Building2}
            loading={chartsLoading}
          >
            {sessionsBySiteData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-slate-500">
                <p>Bu dönem için veri bulunamadı</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sessionsBySiteData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                  >
                    {sessionsBySiteData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </div>
      )}

      {/* Recent Activity - Admin only */}
      {isAdmin() && !isEffectiveTechnician && (
        <div className="p-6 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Canlı İzleme</h2>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className={`w-2 h-2 rounded-full ${liveData?.online_stations > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
              <span>{liveData?.online_stations || 0} cihaz çevrimiçi</span>
            </div>
          </div>
          
          {/* Live Stats */}
          {liveData && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Wifi className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-slate-500">Bağlı Cihaz</span>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{liveData.online_stations}/{liveData.total_stations}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <BatteryCharging className="h-4 w-4 text-violet-500" />
                  <span className="text-sm text-slate-500">Aktif Şarj</span>
                </div>
                <p className="text-2xl font-bold text-violet-600">{liveData.active_charging}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-slate-500">Anlık Güç</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">{(liveData.total_power_kw || 0).toFixed(1)} kW</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-slate-500">Aylık Tüketim</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">{(liveData.monthly_energy_kwh || 0).toFixed(1)} kWh</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />
                    <path d="M17 4a2 2 0 0 0 2 2a2 2 0 0 0 -2 2a2 2 0 0 0 -2 -2a2 2 0 0 0 2 -2" />
                  </svg>
                  <span className="text-sm text-emerald-700 dark:text-emerald-400">CO₂ Tasarrufu</span>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{(liveData.co2_saved_kg || 0).toFixed(1)} kg</p>
                <p className="text-xs text-emerald-500 mt-1">Bu ay</p>
              </div>
            </div>
          )}

          {/* Live Stations List */}
          {liveData?.live_stations?.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">İstasyon Durumu</h3>
              {liveData.live_stations.map((station, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    station.is_charging ? 'bg-violet-500/10' : station.is_online ? 'bg-emerald-500/10' : 'bg-slate-200'
                  }`}>
                    {station.is_charging ? (
                      <BatteryCharging className="h-5 w-5 text-violet-500" />
                    ) : station.is_online ? (
                      <Wifi className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <WifiOff className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 dark:text-white">{station.station_name}</p>
                    <p className="text-sm text-slate-500">{station.charger_id}</p>
                  </div>
                  <div className="text-right">
                    {station.is_charging ? (
                      <>
                        <p className="text-sm font-medium text-violet-500">Şarj Ediyor</p>
                        <p className="text-xs text-slate-500">{station.current_power_kw} kW</p>
                      </>
                    ) : station.is_online ? (
                      <p className="text-sm font-medium text-emerald-500">Müsait</p>
                    ) : (
                      <p className="text-sm font-medium text-slate-400">Çevrimdışı</p>
                    )}
                  </div>
                  <div className="text-right min-w-[80px]">
                    {station.is_charging && station.session_energy_kwh !== null && station.session_energy_kwh !== undefined ? (
                      <>
                        <p className="text-sm font-medium text-violet-600">{station.session_energy_kwh} kWh</p>
                        <p className="text-xs text-slate-500">Bu oturum</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-slate-400">-</p>
                        <p className="text-xs text-slate-500">Oturum yok</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : stats?.active_sessions > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <div className="w-10 h-10 rounded-full bg-violet-500/10 flex items-center justify-center">
                  <BatteryCharging className="h-5 w-5 text-violet-500" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900 dark:text-white">Aktif Şarj Oturumu</p>
                  <p className="text-sm text-slate-500">{stats.active_sessions} oturum devam ediyor</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-emerald-500">Şarj Ediyor</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Aktif oturum yok</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">Şu anda devam eden şarj oturumu bulunmuyor.</p>
            </div>
          )}
          
          {/* Aktif Şarj Oturumları Tablosu */}
          {liveData?.active_sessions?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Aktif Şarj İşlemleri</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-2 px-3 font-medium text-slate-500">İstasyon</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Kullanıcı</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Başlangıç</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">Süre</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">Anlık Güç</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">Tüketim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveData.active_sessions.map((session, idx) => (
                      <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-2 px-3 font-medium text-slate-900 dark:text-white">{session.station_name}</td>
                        <td className="py-2 px-3 text-slate-600 dark:text-slate-400">{session.user_name || session.id_tag || 'Anonim'}</td>
                        <td className="py-2 px-3 text-slate-600 dark:text-slate-400">
                          {session.start_time ? new Date(session.start_time).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'}) : '-'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-600 dark:text-slate-400">{session.duration_mins} dk</td>
                        <td className="py-2 px-3 text-right font-medium text-amber-600">{session.current_power_kw} kW</td>
                        <td className="py-2 px-3 text-right font-medium text-violet-600">{session.energy_kwh} kWh</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
